import { RefObject, useCallback, useEffect, useRef } from 'react';

const scrollSpeedMultiplier = 1;
const DRAG_CLICK_SUPPRESS_THRESHOLD = 5;

type UseGrabAndScrollResult = {
  hasDraggedRef: RefObject<boolean>;
};

export const useGrabAndScroll = (ref: RefObject<HTMLElement | null>, enabled = true): UseGrabAndScrollResult => {
  const isInternalDragging = useRef(false);
  const dragStartPositionX = useRef(0);
  const initialScrollLeft = useRef(0);
  const hasDraggedRef = useRef(false);

  const handleDragMove = useCallback(
    (event: MouseEvent) => {
      const element = ref.current;
      if (!element || !isInternalDragging.current) return;

      event.preventDefault();
      const currentMousePositionX = event.clientX - element.offsetLeft;
      const dragDistance = currentMousePositionX - dragStartPositionX.current;
      if (Math.abs(dragDistance) > DRAG_CLICK_SUPPRESS_THRESHOLD) {
        hasDraggedRef.current = true;
      }
      element.scrollLeft = initialScrollLeft.current - dragDistance * scrollSpeedMultiplier;
    },
    [ref],
  );

  const handleDragEnd = useCallback(() => {
    isInternalDragging.current = false;
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
  }, [handleDragMove]);

  const handleDragStart = useCallback(
    (event: MouseEvent) => {
      const element = ref.current;
      if (!element) return;

      isInternalDragging.current = true;
      hasDraggedRef.current = false;
      dragStartPositionX.current = event.clientX - element.offsetLeft;
      initialScrollLeft.current = element.scrollLeft;

      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
    },
    [ref, handleDragMove, handleDragEnd],
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    element.addEventListener('mousedown', handleDragStart);

    return () => {
      element.removeEventListener('mousedown', handleDragStart);
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [handleDragStart, handleDragMove, handleDragEnd, ref, enabled]);

  return { hasDraggedRef };
};
